import { useState, useEffect } from "react";
import SimplePdfViewer from "./SimplePdfViewer"; // PDF viewer component
import { Modal, Dropdown, DropdownButton, Button, Form, Spinner, Tooltip, OverlayTrigger } from "react-bootstrap";
import "../styles/pdf-modal.css";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faLanguage,
  faChevronLeft,
  faChevronRight,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { highlightText, highlightMatchingLines, downloadTextFile, generateArabicTranscript, generateTranslatedTranscript } from "../utils/helpers";
import useTranslation from "../custom-hooks/useTranslation";
import useDocument from "../custom-hooks/useDocument";
import { feedbackAPI } from "../axios/feedback_axios";

const TOAST_IDS = {
  copySuccess: "simple-pdf-copy-success",
  noContent: "simple-pdf-no-content",
  pageError: "simple-pdf-page-error",
  feedbackSuccess: "simple-pdf-feedback-success",
  feedbackError: "simple-pdf-feedback-error",
  feedbackMissingDetails: "simple-pdf-feedback-missing-details",
  invalidPage: "simple-pdf-invalid-page",
};

// Tooltip for download and copy buttons
const renderTooltip = (props, message) => (
  <Tooltip id="button-tooltip" {...props}>
    {message}
  </Tooltip>
);

const TooltipActionButton = ({ tooltipMessage, icon, children, className = "", ...buttonProps }) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 250, hide: 400 }}
    overlay={(props) => renderTooltip(props, tooltipMessage)}
  >
    <Button className={`ActionButton ${className}`.trim()} {...buttonProps}>
      {icon ? <FontAwesomeIcon icon={icon} /> : null}
      {children}
    </Button>
  </OverlayTrigger>
);

// Components for better organization
const TranslationControls = ({ selectedLanguage, handleLanguageChange, handleTranslateClick }) => (
  <div className="TranslationControls">
    <DropdownButton 
      variant="outline-primary"
      title={`Language: ${selectedLanguage.toUpperCase()}`}
      onSelect={handleLanguageChange}
      className="LanguageDropdown"
    >
      <Dropdown.Item eventKey="en">English (EN)</Dropdown.Item>
      <Dropdown.Item eventKey="fr">French (FR)</Dropdown.Item>
      <Dropdown.Item eventKey="pt">Portuguese (PT)</Dropdown.Item>
      <Dropdown.Item eventKey="es">Spanish (ES)</Dropdown.Item>
    </DropdownButton>

    <Button variant="primary" className="TranslateButton" onClick={handleTranslateClick}>
      <FontAwesomeIcon icon={faLanguage} /> Translate
    </Button>
  </div>
);

const PageNavigation = ({
  currentPage,
  numberOfPages,
  handlePageChange,
  isDocumentLoading,
  isPdfReady,
}) => {
  const [targetPage, setTargetPage] = useState(
    currentPage ? String(currentPage) : ""
  );
  const totalPages = Number(numberOfPages) || 0;

  useEffect(() => {
    // Keep the input in sync when user navigates with arrows
    setTargetPage(currentPage ? String(currentPage) : "");
  }, [currentPage]);

  const handleGoToPage = () => {
    if (!targetPage) return;

    const pageNumber = parseInt(targetPage, 10);

    if (
      Number.isNaN(pageNumber) ||
      pageNumber < 1 ||
      (totalPages > 0 && pageNumber > totalPages)
    ) {
      if (totalPages > 0) {
        toast.warn(`Please enter a page between 1 and ${totalPages}.`, {
          position: "top-center",
          autoClose: 2500,
          toastId: TOAST_IDS.invalidPage,
        });
      }
      return;
    }

    handlePageChange(pageNumber);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleGoToPage();
    }
  };

  const isNavDisabled = isDocumentLoading || !isPdfReady || totalPages === 0;

  return (
    <div className="navigation-controls">
      <div className="nav-group">
        <Button
          variant="outline-secondary"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isNavDisabled}
          size="sm"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <span className="page-indicator">
          Page{" "}
          <Form.Control
            type="number"
            min={1}
            max={totalPages || undefined}
            value={targetPage}
            onChange={(event) => setTargetPage(event.target.value)}
            onKeyDown={handleKeyDown}
            size="sm"
            disabled={isNavDisabled}
            className="InlinePageInput"
          />{" "}
          of {totalPages || numberOfPages || "-"}
        </span>
        <Button
          variant="outline-secondary"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || isNavDisabled}
          size="sm"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>
    </div>
  );
};

const FEEDBACK_TYPES = [
  { value: "transcription_error", label: "Transcription error" },
  { value: "translation_error", label: "Translation error" },
  { value: "incorrect_description", label: "Incorrect description" },
  { value: "image_quality", label: "Image not clear" },
  { value: "other", label: "Other feedback" },
];

const SimplePdfViewerModal = ({ show, handleClose, selectedRow, searchQuery }) => {
  const [pdfState, setPdfState] = useState({
    url: '',
    lines: [],
    selectedLine: null,
    imageSize: null,
    currentPage: 1
  });

  const [metadata, setMetadata] = useState({
    title: '',
    date: '',
    description: '',
    numberOfPages: ''
  });

  const [textContent, setTextContent] = useState({
    arabicBlock: "",
    highlightedLines: [],
    selectedLanguage: "en",
    isLineView: false
  });

  // State to track active document
  const [currentDocumentId, setCurrentDocumentId] = useState(selectedRow?._id || '');
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [isPdfReady, setIsPdfReady] = useState(false);

  const [showFeedbackPane, setShowFeedbackPane] = useState(false);
  const [feedbackType, setFeedbackType] = useState(FEEDBACK_TYPES[0].value);
  const [feedbackDetails, setFeedbackDetails] = useState("");

  const [highlightCoords, setHighlightCoords] = useState(() => {
    // Get existing coordinates from the parent component or state management
    return selectedRow?.highlightCoords || {
      line: [],
      search: selectedRow?.searchCoords || []
    };
  });

  const { translatedBlock, setTranslatedBlock, translateText } = useTranslation();
  const { getDocument } = useDocument();

  // Load initial data
  useEffect(() => {
    if (selectedRow) {
      const pageNumber = selectedRow.page_number || 1;
      const extractedLines = Object.values(selectedRow.line_info).map(line => ({
        text: line.text,
        coord: line.coord.flat()
      }));
      
      const baseUrl = selectedRow.file_url.split('_').slice(0, -1).join('_');
      
      setPdfState({
        url: `${baseUrl}_${pageNumber}.pdf`,
        lines: extractedLines,
        selectedLine: null,
        imageSize: selectedRow.image_size,
        currentPage: pageNumber
      });
      setIsPdfReady(false);

      setMetadata({
        title: selectedRow.title || '',
        date: selectedRow.date_of_manuscript || '',
        description: selectedRow.description || '',
        numberOfPages: selectedRow.number_of_pages || ''
      });

      setTextContent(prev => ({
        ...prev,
        arabicBlock: selectedRow?.text || ''
      }));

      setCurrentDocumentId(selectedRow._id);
    }
  }, [selectedRow]);

  // Search highlighting effect
  useEffect(() => {
    if (pdfState.lines.length > 0 && searchQuery) {
      highlightMatchingLines(pdfState.lines, searchQuery, coords => 
        setHighlightCoords(prev => ({ ...prev, search: coords }))
      );
    } else {
      setHighlightCoords(prev => ({ ...prev, search: [] }));
    }
  }, [pdfState.lines, searchQuery]);


  const handleClosePdfModal = () => {
    setShowFeedbackPane(false);
    setTranslatedBlock("");
    // clearTranslationCache();
    setTextContent(prev => ({ 
      ...prev, 
      selectedLanguage: "en", 
      isLineView: false,
      arabicBlock: selectedRow?.text || ''
    }));
    setPdfState(prev => ({
      ...prev,
      lines: Object.values(selectedRow?.line_info || {}).map(line => ({
        text: line.text,
        coord: line.coord.flat()
      })),
      selectedLine: null,
      currentPage: selectedRow?.page_number || 1,
      url: selectedRow?.file_url || ""
    }));
    setIsPdfReady(false);
    setCurrentDocumentId(selectedRow?._id || '')
    handleClose();
  };

  const handleLineClick = (line, index) => {
    setPdfState(prev => ({ ...prev, selectedLine: index }));
    setHighlightCoords(prev => ({ 
      ...prev, 
      line: line.coord || [] 
    }));
  };

  const handleLanguageChange = language => {
    setTextContent(prev => ({ ...prev, selectedLanguage: language }));
  };

  const handleTranslateClick = () => {
    translateText(currentDocumentId, textContent.selectedLanguage);
  };

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!", {
      position: "top-center",
      autoClose: 2000,
      toastId: TOAST_IDS.copySuccess,
    });
};

  const handleDownload = (type) => {
    const content = type === 'arabic' ? textContent.arabicBlock : translatedBlock;
    const language = type === 'arabic' ? 'arabic' : textContent.selectedLanguage;
    
    if (!content) {
      toast.warn(`No ${language.toUpperCase()} content to download.`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        toastId: TOAST_IDS.noContent,
      });
      return;
    }

    const transcriptContent = type === 'arabic' 
      ? generateArabicTranscript(content)
      : generateTranslatedTranscript(content, language);
    downloadTextFile(transcriptContent, `${metadata.title}_${type}_transcript${type === 'translated' ? '_' + language : ''}.txt`);
  };

  const handlePageChange = async (pageNumber) => {
    if (!currentDocumentId) {
      console.error('No document ID found');
      return;
    }

    if (pageNumber >= 1 && pageNumber <= metadata.numberOfPages) {
      try {
        setIsDocumentLoading(true)
        setIsPdfReady(false);
        // Update PDF URL first
        const prefix = pdfState.url.split('_').slice(0, -1).join('_');
        setPdfState(prev => ({
          ...prev,
          currentPage: pageNumber,
          selectedLine: null,
          url: `${prefix}_${pageNumber}.pdf`
        }));

        const newDocumentIdPrefix = currentDocumentId.split('_').slice(0, -1).join('_');
        const newDocumentId = `${newDocumentIdPrefix}_${pageNumber}.pdf`;
        const updatedDocument = await getDocument(newDocumentId);

        if (updatedDocument) {
          setTextContent(prev => ({
            ...prev,
            arabicBlock: updatedDocument.text || ''
          }));

          const extractedLines = Object.values(updatedDocument.line_info || {}).map(line => ({
            text: line.text,
            coord: line.coord.flat()
          }));

          setPdfState(prev => ({
            ...prev,
            url: updatedDocument.file_url,
            lines: extractedLines
          }));

          setCurrentDocumentId(newDocumentId);

          if (translatedBlock !== "") {
            await translateText(newDocumentId, textContent.selectedLanguage);
          }

          if (searchQuery && document.line_info) {
            highlightMatchingLines(extractedLines, searchQuery, coords => 
              setHighlightCoords(prev => ({ ...prev, search: coords }))
            );
          }
        }
      } catch (error) {
        console.error('Error changing page:', error);
        toast.error("Error changing page.", {
          position: "top-center",
          autoClose: 3000,
          toastId: TOAST_IDS.pageError,
        });
      } finally {
          setIsDocumentLoading(false);
      }
    }
  };

  const handleOpenFeedbackModal = () => {
    setFeedbackType(FEEDBACK_TYPES[0].value);
    setFeedbackDetails("");
    setShowFeedbackPane(true);
  };

  const handleFeedbackSubmit = async () => {
    const pageNumber = pdfState.currentPage || selectedRow?.page_number || 1;
    const selectedLineText =
      pdfState.selectedLine !== null && pdfState.selectedLine >= 0
        ? pdfState.lines[pdfState.selectedLine]?.text
        : null;

    if (!feedbackDetails.trim()) {
      toast.warn("Please provide details for the issue you are reporting.", {
        position: "top-center",
        autoClose: 2500,
        toastId: TOAST_IDS.feedbackMissingDetails,
      });
      return;
    }

    const feedbackPayload = {
      feedbackType,
      details: feedbackDetails.trim(),
      pageNumber,
      document: {
        id: selectedRow?._id || currentDocumentId,
      },
    };

    try {
      await feedbackAPI.post("/", feedbackPayload);
      toast.success("Feedback submitted successfully.", {
        position: "top-center",
        autoClose: 2500,
        toastId: TOAST_IDS.feedbackSuccess,
      });
      setShowFeedbackPane(false);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toast.error("Failed to submit feedback.", {
        position: "top-center",
        autoClose: 3000,
        toastId: TOAST_IDS.feedbackError,
      });
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackPane(false);
  };

  const handleViewToggle = (checked) => {
    setTextContent(prev => ({ ...prev, isLineView: checked }));
    if (!checked) {
      setPdfState(prev => ({ ...prev, selectedLine: null }));
      setHighlightCoords(prev => ({ ...prev, line: [] }));
    }
  };

  return (
    <Modal show={show} onHide={handleClosePdfModal} centered dialogClassName="my-modal">
      <Modal.Header closeButton>
        <Modal.Title>{metadata.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="ModalBodyStyle">
        {/* Section for Title, Date, Description, and Number of Pages */}
        <div className="ModalHeader">
          <p className="ModalDate">Date: {metadata.date}</p>
          <p className="ModalDescription">Description: {metadata.description}</p>
          <p className="ModalPages">Pages: {metadata.numberOfPages}</p>
        </div>

        <TranslationControls 
          selectedLanguage={textContent.selectedLanguage}
          handleLanguageChange={handleLanguageChange}
          handleTranslateClick={handleTranslateClick}
        />

        <div className="SplitView">
          {/* Translated Column */}
          {translatedBlock && (
            <div className="TranslatedTextContainer">
              <div className="ButtonRow">
                <TooltipActionButton
                  tooltipMessage="Download"
                  variant="outline-primary"
                  onClick={() => handleDownload('translated')}
                  icon={faDownload}
                />
                <TooltipActionButton
                  tooltipMessage="Copy"
                  variant="outline-primary"
                  onClick={() => handleCopy(translatedBlock)}
                  icon={faCopy}
                />
              </div>
              <div className="TranslatedTextBlock">
                <p>{translatedBlock}</p>
              </div>
            </div>
          )}
          {/* Arabic Column */}
          <div className="ArabicTextContainer">
            <div className="ButtonRow">
              <TooltipActionButton
                tooltipMessage="Download"
                variant="outline-primary"
                onClick={() => handleDownload('arabic')}
                icon={faDownload}
              />
              <TooltipActionButton
                tooltipMessage="Copy"
                variant="outline-primary"
                onClick={() => handleCopy(textContent.arabicBlock)}
                icon={faCopy}
              />
              <div className="view-toggle-container">
                <Form.Check
                  type="switch"
                  id="view-toggle"
                  checked={textContent.isLineView}
                  onChange={(e) => handleViewToggle(e.currentTarget.checked)}
                  label="Line View"
                  className="view-toggle-switch"
                />
              </div>
            </div>

            {textContent.isLineView ? (
              <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
                {pdfState.lines.map((line, index) => (
                  <li
                    key={index}
                    className={`TextLine ${pdfState.selectedLine === index ? "selected" : ""} ${
                      textContent.highlightedLines.includes(index) ? "highlighted" : ""
                    }`}
                    onClick={() => handleLineClick(line, index)}
                  >
                    {highlightText(line.text, searchQuery)}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ArabicTextBlock">
                <p>{highlightText(textContent.arabicBlock, searchQuery)}</p>
              </div>
            )}
          </div>

          <div className="PdfViewerContainer">
          <PageNavigation 
            currentPage={pdfState.currentPage}
            numberOfPages={metadata.numberOfPages}
            handlePageChange={handlePageChange}
            isDocumentLoading={isDocumentLoading}
            isPdfReady={isPdfReady}
          />
            {pdfState.url 
            ? (
              <SimplePdfViewer
                // key={window.location.href}
                pdfUrl={pdfState.url}
                highlightCoords={highlightCoords.line}
                searchCoords={highlightCoords.search}
                imageSize={pdfState.imageSize}
                selectedLine={pdfState.selectedLine}
                onDocumentLoad={() => setIsPdfReady(true)}
                onDocumentLoadFail={() => setIsPdfReady(false)}
              />
            ) : (
              <div style={{ textAlign: "center", marginTop: "2em" }}>
                <Spinner animation="border" variant="primary" role="status">
                  <span className="visually-hidden">Loading Manuscript...</span>
                </Spinner>
              </div>
            )}
          </div>

          {showFeedbackPane && (
            <div className="FeedbackPaneInline">
              <div className="FeedbackPaneHeader">
                <h5>Report Issue</h5>
                <Button variant="link" size="sm" onClick={handleFeedbackCancel}>
                  ✕
                </Button>
              </div>
              <div className="FeedbackSummary">
                <div><strong>Title:</strong> {metadata.title || "Untitled"}</div>
                <div>
                  <strong>Current page:</strong> {pdfState.currentPage}
                  {metadata.numberOfPages ? ` of ${metadata.numberOfPages}` : ""}
                </div>
                <div>
                  <strong>Translation language:</strong>{" "}
                  {textContent.selectedLanguage?.toUpperCase()}
                </div>
              </div>
              <Form.Group className="mb-3" controlId="feedbackType">
                <Form.Label>What is the issue you are reporting?</Form.Label>
                <Form.Select
                  value={feedbackType}
                  onChange={(event) => setFeedbackType(event.target.value)}
                >
                  {FEEDBACK_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="feedbackDetails">
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  placeholder="Tell us about the transcription/translation issue or any other feedback."
                  value={feedbackDetails}
                  onChange={(event) => setFeedbackDetails(event.target.value)}
                />
              </Form.Group>
              <div className="FeedbackPaneActions">
                <Button variant="outline-secondary" onClick={handleFeedbackCancel}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleFeedbackSubmit}>
                  Submit
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="FooterStyle">
        <button className="FeedbackButton" onClick={handleOpenFeedbackModal}>
          Report Issue
        </button>
        <button className="CloseButton" onClick={handleClosePdfModal}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default SimplePdfViewerModal;
